import { getCase } from "../../content/lookups.js";
import type { NodeDefinition, NodeId, Screen } from "../../domain/types/index.js";
import { edgeEndpoints } from "./mapLayout.js";
import {
  mapCanvasHeight,
  mapDensity,
  mapGridTemplateColumns,
  mapRowHeight,
} from "./mapDensity.js";
import { mapNodeVisual } from "./mapPeek.js";
import { MapNode } from "./MapNode.js";
import { buildMapView } from "./mapView.js";

type Props = {
  run: {
    stageId?: string;
    currentNodeId: NodeId;
    availableNodeIds: readonly NodeId[];
    clearedNodeIds: readonly NodeId[];
    activeCase?: { nodeId: NodeId } | null;
  };
  screen: Screen;
  selectedId: NodeId | null;
  onSelect: (node: NodeDefinition) => void;
};

export function MapGraph({ run, screen, selectedId, onSelect }: Props) {
  const { nodes, layout, edges, byId } = buildMapView(run.stageId);
  const density = mapDensity(layout.columns);
  const rowHeight = mapRowHeight(density);
  const canvasHeight = mapCanvasHeight(layout.totalRows, density);
  return (
    <div className={`map-wrap is-${density}`} style={{ minHeight: canvasHeight }}>
      <svg
        className="map-lines"
        viewBox={`0 0 ${layout.columns} ${layout.totalRows}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        style={{ height: canvasHeight }}
      >
        <defs>
          <marker
            id="map-route-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="0.16"
            markerHeight="0.16"
            markerUnits="userSpaceOnUse"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" />
          </marker>
        </defs>
        {edges.map((edge) => {
          const from = byId.get(edge.from);
          const to = byId.get(edge.to);
          if (!from || !to) return null;
          const { x1, y1, x2, y2 } = edgeEndpoints(from, to, layout);
          return (
            <line
              key={`${edge.from}-${edge.to}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              markerEnd="url(#map-route-arrow)"
            />
          );
        })}
      </svg>
      <div
        className="map-grid"
        style={{
          gridTemplateColumns: mapGridTemplateColumns(layout.columns),
          gridTemplateRows: `repeat(${layout.totalRows}, ${rowHeight}px)`,
        }}
      >
        {nodes.map((node) => (
          <MapNode
            key={node.id}
            node={node}
            spec={(node.caseId ? getCase(node.caseId) : null) ?? null}
            visual={mapNodeVisual(node.id, run, screen)}
            selected={node.id === selectedId}
            layout={layout}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

export { nodeById } from "./mapView.js";
